import React from "react";
import { formatarMoeda } from "../../utils/calculos";
import { breakdownTone } from "./helpers";
import {
  BreakdownCard,
  BreakdownCount,
  BreakdownLabel,
  BreakdownList,
  KpiCard,
  KpiFaixa,
  KpiGrid,
  KpiLabel,
  KpiValue,
  NextActionBody,
  NextActionButton,
  NextActionCard,
  NextActionLabel,
  NextActionTitle,
  RailCard,
  RailTitle,
} from "./styles";

const HIDDEN_VALUE_LABEL = "******";

function formatBreakdownSub(entry) {
  const parts = [];
  if (entry.vencidos > 0) parts.push(`${entry.vencidos} vencido${entry.vencidos === 1 ? "" : "s"}`);
  if (entry.hoje > 0) parts.push(`${entry.hoje} hoje`);
  if (entry.semana > 0) parts.push(`${entry.semana} essa sem.`);
  if (parts.length === 0 && entry.total > 0) parts.push("todos no prazo");
  if (parts.length === 0) parts.push("nenhum pendente");
  return parts.join(" · ");
}

// Celular/tablet: sem rail lateral. A próxima ação fica enxuta (o corpo some
// via CSS) e indicadores + tipos viram uma faixa rolável de tiles.
function RailCompacto({
  nextAction,
  breakdown,
  kpis,
  precosVisiveis,
  selectedTipos,
  onToggleTipo,
  onNextActionCta,
}) {
  const indicadores = [
    { id: "amb", label: "Ambientes", valor: kpis.qtdAmbientes, tone: "default" },
    { id: "itens", label: "Itens", valor: kpis.itensTotal, tone: "info" },
    {
      id: "atrasados",
      label: "Atrasados",
      valor: kpis.ambientesAtrasados,
      tone: kpis.ambientesAtrasados > 0 ? "danger" : "default",
    },
    {
      id: "dias",
      label: "Dias na fila",
      valor: kpis.diasMedios,
      tone: kpis.diasMedios > 7 ? "warning" : "default",
    },
    {
      id: "valor",
      label: "Valor parado",
      valor: precosVisiveis ? formatarMoeda(kpis.valorParado) : HIDDEN_VALUE_LABEL,
      tone: "default",
    },
  ];
  return (
    <>
      {nextAction && (
        <NextActionCard $tone={nextAction.tone}>
          <NextActionLabel>Próxima ação</NextActionLabel>
          <NextActionTitle>{nextAction.title}</NextActionTitle>
          {nextAction.ctaLabel && nextAction.cta && (
            <NextActionButton type="button" onClick={() => onNextActionCta?.(nextAction.cta)}>
              {nextAction.ctaLabel}
            </NextActionButton>
          )}
        </NextActionCard>
      )}
      <KpiFaixa role="group" aria-label="Indicadores">
        {indicadores.map((kpi) => (
          <KpiCard key={kpi.id} $tone={kpi.tone}>
            <KpiLabel className="kpi-label">{kpi.label}</KpiLabel>
            <KpiValue className="kpi-num">{kpi.valor}</KpiValue>
          </KpiCard>
        ))}
        {breakdown.map((entry) => {
          const isDisabled = entry.total === 0;
          return (
            <BreakdownCard
              key={entry.id}
              type="button"
              $tone={breakdownTone(entry)}
              $disabled={isDisabled}
              disabled={isDisabled}
              aria-pressed={selectedTipos.has(entry.id)}
              title={formatBreakdownSub(entry)}
              onClick={() => !isDisabled && onToggleTipo?.(entry.id)}
            >
              <BreakdownLabel>
                <strong className="kpi-label">{entry.label}</strong>
              </BreakdownLabel>
              <BreakdownCount className="kpi-num">{entry.total}</BreakdownCount>
            </BreakdownCard>
          );
        })}
      </KpiFaixa>
    </>
  );
}

export default function ContextRail({
  nextAction,
  breakdown,
  kpis,
  precosVisiveis,
  selectedTipos,
  onToggleTipo,
  onNextActionCta,
  compacto = false,
}) {
  if (compacto) {
    return (
      <RailCompacto
        nextAction={nextAction}
        breakdown={breakdown}
        kpis={kpis}
        precosVisiveis={precosVisiveis}
        selectedTipos={selectedTipos}
        onToggleTipo={onToggleTipo}
        onNextActionCta={onNextActionCta}
      />
    );
  }
  return (
    <>
      {nextAction && (
        <NextActionCard $tone={nextAction.tone}>
          <NextActionLabel>Próxima ação</NextActionLabel>
          <NextActionTitle>{nextAction.title}</NextActionTitle>
          {nextAction.body && <NextActionBody>{nextAction.body}</NextActionBody>}
          {nextAction.ctaLabel && nextAction.cta && (
            <NextActionButton
              type="button"
              data-export-no-print
              onClick={() => onNextActionCta?.(nextAction.cta)}
            >
              {nextAction.ctaLabel}
            </NextActionButton>
          )}
        </NextActionCard>
      )}

      <RailCard>
        <RailTitle>Indicadores</RailTitle>
        <KpiGrid>
          <KpiCard $tone="default">
            <KpiLabel>Ambientes</KpiLabel>
            <KpiValue>{kpis.qtdAmbientes}</KpiValue>
          </KpiCard>
          <KpiCard $tone="info">
            <KpiLabel>Itens pendentes</KpiLabel>
            <KpiValue>{kpis.itensTotal}</KpiValue>
          </KpiCard>
          <KpiCard $tone={kpis.ambientesAtrasados > 0 ? "danger" : "default"}>
            <KpiLabel>Atrasados</KpiLabel>
            <KpiValue>{kpis.ambientesAtrasados}</KpiValue>
          </KpiCard>
          <KpiCard $tone={kpis.diasMedios > 7 ? "warning" : "default"}>
            <KpiLabel>Dias médios</KpiLabel>
            <KpiValue>{kpis.diasMedios}</KpiValue>
          </KpiCard>
        </KpiGrid>
        <KpiCard $tone="default">
          <KpiLabel>Valor parado</KpiLabel>
          <KpiValue>
            {precosVisiveis ? formatarMoeda(kpis.valorParado) : HIDDEN_VALUE_LABEL}
          </KpiValue>
        </KpiCard>
      </RailCard>

      <RailCard>
        <RailTitle>Por tipo de terceirizado</RailTitle>
        <BreakdownList>
          {breakdown.map((entry) => {
            const tone = breakdownTone(entry);
            const isActive = selectedTipos.has(entry.id);
            const isDisabled = entry.total === 0;
            return (
              <BreakdownCard
                key={entry.id}
                type="button"
                $tone={tone}
                $disabled={isDisabled}
                disabled={isDisabled}
                aria-pressed={isActive}
                onClick={() => !isDisabled && onToggleTipo?.(entry.id)}
              >
                <BreakdownLabel>
                  <strong>{entry.label}</strong>
                  <span>{formatBreakdownSub(entry)}</span>
                </BreakdownLabel>
                <BreakdownCount>{entry.total}</BreakdownCount>
              </BreakdownCard>
            );
          })}
        </BreakdownList>
      </RailCard>
    </>
  );
}
